<template>
  <div class="modal-overlay">
    <div class="modal">
      <div class="modal-header">
        <h2>{{ $t('bo.sessionTemplates.createTitle') }}</h2>
        <button @click="$emit('close')" class="btn-close">&times;</button>
      </div>

      <div class="modal-body">
        <div v-if="error" class="error-banner">{{ error }}</div>

        <div class="form-group">
          <label>{{ $t('bo.sessionTemplates.labelTitle') }} <span class="required">*</span></label>
          <input v-model="form.title" type="text" class="input" :placeholder="$t('bo.sessionTemplates.titlePlaceholder')" maxlength="200" />
        </div>

        <div class="form-group">
          <label>{{ $t('bo.sessionTemplates.labelDescription') }}</label>
          <textarea v-model="form.description" class="textarea" rows="3" :placeholder="$t('bo.sessionTemplates.descriptionPlaceholder')" />
        </div>

        <div class="form-group">
          <label>{{ $t('bo.sessionTemplates.labelDuration') }}</label>
          <input v-model.number="form.estimated_duration" type="number" class="input input-sm" min="1" max="480" :placeholder="$t('bo.sessionTemplates.durationPlaceholder')" />
        </div>

        <div class="form-group">
          <label>{{ $t('bo.sessionTemplates.labelPrompts') }}</label>
          <div class="tag-input-area">
            <div class="tag-list">
              <span v-for="(p, i) in form.discussion_prompts" :key="i" class="tag-item">
                {{ p }}
                <button @click="removePrompt(i)" class="tag-remove">&times;</button>
              </span>
            </div>
            <input
              v-model="promptInput"
              @keydown.enter.prevent="addPrompt"
              type="text"
              class="input"
              :placeholder="$t('bo.sessionTemplates.pressEnterToAdd')"
            />
          </div>
        </div>

        <div class="form-group">
          <label>{{ $t('bo.sessionTemplates.labelCompetencies') }}</label>
          <div class="tag-input-area">
            <div class="tag-list">
              <span v-for="(c, i) in form.competencies" :key="i" class="tag-item">
                {{ c }}
                <button @click="removeCompetency(i)" class="tag-remove">&times;</button>
              </span>
            </div>
            <input
              v-model="competencyInput"
              @keydown.enter.prevent="addCompetency"
              type="text"
              class="input"
              :placeholder="$t('bo.sessionTemplates.pressEnterToAdd')"
            />
          </div>
        </div>

        <div class="form-group">
          <label>{{ $t('bo.sessionTemplates.labelTags') }}</label>
          <div class="tag-input-area">
            <div class="tag-list">
              <span v-for="(t, i) in form.tags" :key="i" class="tag-item">
                {{ t }}
                <button @click="removeTag(i)" class="tag-remove">&times;</button>
              </span>
            </div>
            <input
              v-model="tagInput"
              @keydown.enter.prevent="addTag"
              type="text"
              class="input"
              :placeholder="$t('bo.sessionTemplates.pressEnterToAdd')"
            />
          </div>
        </div>

        <div class="section-divider">{{ $t('bo.sessionTemplates.sectionMilestone') }}</div>

        <div class="form-group">
          <label>{{ $t('bo.sessionTemplates.labelMilestoneTitle') }}</label>
          <input v-model="form.meta_data.milestone_title" type="text" class="input" :placeholder="$t('bo.sessionTemplates.milestoneTitlePlaceholder')" maxlength="200" />
        </div>

        <div class="form-group">
          <label>{{ $t('bo.sessionTemplates.labelMilestoneDueDate') }}</label>
          <input v-model="form.meta_data.milestone_due_date" type="date" class="input input-sm" />
        </div>

        <SessionTaskPanel ref="taskPanel" :tasks="form.tasks" @change="form.tasks = $event" />
      </div>

      <div class="modal-footer">
        <button @click="$emit('close')" class="btn-cancel">{{ $t('bo.common.cancel') }}</button>
        <button @click="submit" :disabled="loading || !form.title.trim()" class="btn-submit">
          {{ loading ? $t('bo.common.creating') : $t('bo.sessionTemplates.createTemplate') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import sessionTemplateService from '@/services/sessionTemplateService'
import SessionTaskPanel from '@/components/common/SessionTaskPanel.vue'
import { toTasksPayload } from '@/stores/adminProgramCreation'

const { t } = useI18n()

const emit = defineEmits(['close', 'created'])

const loading = ref(false)
const error = ref('')
const promptInput = ref('')
const competencyInput = ref('')
const tagInput = ref('')
const taskPanel = ref(null)

const form = ref({
  title: '',
  description: '',
  estimated_duration: null,
  discussion_prompts: [],
  competencies: [],
  tags: [],
  tasks: [],
  meta_data: {
    milestone_title: '',
    milestone_due_date: '',
  },
})

const addPrompt = () => {
  const val = promptInput.value.trim()
  if (val && !form.value.discussion_prompts.includes(val)) {
    form.value.discussion_prompts.push(val)
    promptInput.value = ''
  }
}

const removePrompt = (i) => form.value.discussion_prompts.splice(i, 1)

const addCompetency = () => {
  const val = competencyInput.value.trim()
  if (val && !form.value.competencies.includes(val)) {
    form.value.competencies.push(val)
    competencyInput.value = ''
  }
}

const removeCompetency = (i) => form.value.competencies.splice(i, 1)

const addTag = () => {
  const val = tagInput.value.trim()
  if (val && !form.value.tags.includes(val)) {
    form.value.tags.push(val)
    tagInput.value = ''
  }
}

const removeTag = (i) => form.value.tags.splice(i, 1)

const submit = async () => {
  if (!form.value.title.trim()) return
  // A task still being typed is added first (title required), as in the program builder.
  if (taskPanel.value && !taskPanel.value.commitDraft()) return
  loading.value = true
  error.value = ''
  try {
    const payload = { ...form.value, tasks: toTasksPayload(form.value.tasks) }
    if (!payload.estimated_duration) delete payload.estimated_duration
    if (!payload.description) delete payload.description
    if (!payload.meta_data.milestone_title && !payload.meta_data.milestone_due_date) {
      delete payload.meta_data
    }
    await sessionTemplateService.createTemplate(payload)
    emit('created')
  } catch (e) {
    error.value = e.response?.data?.message || t('bo.sessionTemplates.failedCreate')
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
}

.modal {
  background: white;
  border-radius: 0.75rem;
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.modal-header h2 {
  font-size: 1.125rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
}

.btn-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6b7280;
  line-height: 1;
  padding: 0 0.25rem;
}

.modal-body {
  padding: 1.5rem;
  overflow-y: auto;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.error-banner {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 0.75rem 1rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

.form-group label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
}

.required { color: #dc2626; }

.input {
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  color: #1f2937;
  width: 100%;
  box-sizing: border-box;
}

.input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59,130,246,0.1);
}

.input-sm { max-width: 160px; }

.textarea {
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  color: #1f2937;
  resize: vertical;
  font-family: inherit;
}

.textarea:focus {
  outline: none;
  border-color: #3b82f6;
}

.tag-input-area {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.tag-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
  min-height: 1rem;
}

.tag-item {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.6rem;
  background: #eff6ff;
  color: #1d4ed8;
  border-radius: 9999px;
  font-size: 0.8rem;
}

.tag-remove {
  background: none;
  border: none;
  cursor: pointer;
  color: #1d4ed8;
  font-size: 1rem;
  line-height: 1;
  padding: 0;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1.25rem 1.5rem;
  border-top: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.btn-cancel {
  padding: 0.5rem 1rem;
  border: 1px solid #d1d5db;
  background: white;
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.875rem;
  color: #374151;
}

.btn-cancel:hover { background: #f9fafb; }

.btn-submit {
  padding: 0.5rem 1.25rem;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 600;
}

.btn-submit:hover:not(:disabled) { background: #2563eb; }
.btn-submit:disabled { opacity: 0.5; cursor: not-allowed; }

.section-divider {
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #9ca3af;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #e5e7eb;
  margin-top: 0.25rem;
}
</style>
